import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import FoundersNote from "@/components/FoundersNote";
import Logo from "@/components/Logo";
// import AdSlot from '@/components/AdSlot';
// import Tagline from '@/components/Tagline';
import {
  Sparkles,
  Clock,
  Star,
  Shield,
  Zap,
  ArrowRight,
  Brain,
  Camera,
  Check,
  Phone,
  Mail,
  MapPin,
  Users,
  Home,
  ChefHat,
  Shirt,
  Bath,
  Layers,
  Wind,
  Bug,
  Square,
  Instagram,
  Twitter,
  Facebook,
  Linkedin,
  ArrowUp,
  CalendarDays,
  // Trophy, — used only by the hidden leaderboard section
  Send,
  Rocket,
  ScanLine,
  Wand2,
  MessageSquare,
  Loader2,
  BadgeCheck,
  HelpCircle,
  IndianRupee,
  Timer,
  ChevronDown,
  PartyPopper,
  Gift,
  Moon,
  Sun,
  FileText,
  Rabbit,
  Gauge,
  Handshake,
  Truck,
} from "lucide-react";

const ICON_MAP = {
  sparkles: Sparkles,
  "cooking-pot": ChefHat,
  shirt: Shirt,
  bath: Bath,
  home: Home,
  layers: Layers,
  square: Square,
  bug: Bug,
  wind: Wind,
};
const TINT_COLORS = [
  "#F3EEFF",
  "#FFF8E1",
  "#E8FFF5",
  "#FFF0EB",
  "#EDE8FF",
  "#F0FFF4",
  "#FEF3C7",
  "#FDF2F8",
  "#EFF6FF",
];
const ACCENT_COLORS = [
  "#6C4CF1",
  "#FFC93C",
  "#2EE6C9",
  "#FF7A59",
  "#8B5CF6",
  "#059669",
  "#B8860B",
  "#EC4899",
  "#3B82F6",
];

const BEFORE_IMG =
  "https://customer-assets.emergentagent.com/job_quickkaam-ai/artifacts/jtsk9r6k_Screenshot%202026-04-15%20at%201.03.14%20AM.png";
const AFTER_IMG =
  "https://customer-assets.emergentagent.com/job_quickkaam-ai/artifacts/2cokf3h3_Screenshot%202026-04-15%20at%201.04.11%20AM.png";

// eslint-disable-next-line no-unused-vars -- kept for the hidden testimonials section
const TESTIMONIALS = [
  {
    name: "Ananya R.",
    location: "Koramangala",
    rating: 5,
    text: "The AI matching is spot-on every time! My home has never been cleaner.",
    img: "https://i.pravatar.cc/100?img=1",
  },
  {
    name: "Vikram S.",
    location: "HSR Layout",
    rating: 5,
    text: "Booked a full home clean in 30 seconds. Worker was fantastic!",
    img: "https://i.pravatar.cc/100?img=3",
  },
  {
    name: "Priya M.",
    location: "Indiranagar",
    rating: 5,
    text: "Mess detection is brilliant. Snapped a photo, got instant suggestions.",
    img: "https://i.pravatar.cc/100?img=5",
  },
  {
    name: "Rohit K.",
    location: "Whitefield",
    rating: 5,
    text: "Best home services app. Consistent quality and fair prices.",
    img: "https://i.pravatar.cc/100?img=8",
  },
];

const GENIE_PROMPTS = [
  "Had a house party last night — everything's a mess 😅",
  "My AC isn't cooling and I've seen a lizard in the kitchen.",
  "Moving out next week, need the whole flat spotless.",
  "Utensils are piled up and bathroom tiles have turned yellow.",
];

// Fallback catalog — ensures the grids ALWAYS render, even if the API hasn't responded yet
// or if the browser blocks the XHR. This is the source of truth for the initial paint.
const FALLBACK_SERVICES = [
  {
    service_id: "svc_utensils",
    name: "Kitchen & Utensils",
    description: "Kitchen cleanup, utensils wash and sanitization.",
    icon: "cooking-pot",
    price: 150,
    duration: 60,
    booking_count: 892,
    avg_rating: 4.7,
  },
  {
    service_id: "svc_bathroom",
    name: "Bathroom Deep Clean",
    description: "Tile scrubbing, grout cleaning and sanitization.",
    icon: "bath",
    price: 180,
    duration: 60,
    booking_count: 621,
    avg_rating: 4.7,
  },
  {
    service_id: "svc_fullhome",
    name: "Dry & Wet Cleaning",
    description: "Daily dry sweep + wet mop across every room. Regular upkeep.",
    icon: "spray-can",
    price: 200,
    duration: 80,
    booking_count: 456,
    avg_rating: 4.9,
  },
  {
    service_id: "svc_deepclean",
    name: "Full Home Deep Clean",
    description:
      "Machine-powered deep clean — every room and furniture included.",
    icon: "home",
    price: 3500,
    duration: 240,
    booking_count: 124,
    avg_rating: 4.9,
  },
  {
    service_id: "svc_combined",
    name: "Combined Service",
    description: "Utensils + Bathroom + Dry/Wet Clean bundled at a saving.",
    icon: "sparkles",
    price: 450,
    duration: 80,
    booking_count: 218,
    avg_rating: 4.9,
  },
];

const FALLBACK_WORKERS = [
  {
    worker_id: "wrk_priya",
    name: "Priya Sharma",
    rating: 4.9,
    jobs_completed: 518,
    image: "https://i.pravatar.cc/150?img=5",
    badge: "Top Rated",
    area: "Bopal",
  },
  {
    worker_id: "wrk_ravi",
    name: "Ravi Kumar",
    rating: 4.8,
    jobs_completed: 342,
    image: "https://i.pravatar.cc/150?img=11",
    badge: "Popular in your area",
    area: "Satellite",
  },
  {
    worker_id: "wrk_kiran",
    name: "Kiran Joshi",
    rating: 4.8,
    jobs_completed: 267,
    image: "https://i.pravatar.cc/150?img=14",
    badge: "AC Specialist",
    area: "Thaltej",
  },
  {
    worker_id: "wrk_amit",
    name: "Amit Singh",
    rating: 4.7,
    jobs_completed: 215,
    image: "https://i.pravatar.cc/150?img=12",
    badge: "",
    area: "Navrangpura",
  },
  {
    worker_id: "wrk_meera",
    name: "Meera Patel",
    rating: 4.6,
    jobs_completed: 289,
    image: "https://i.pravatar.cc/150?img=9",
    badge: "Fast Worker",
    area: "Vastrapur",
  },
  {
    worker_id: "wrk_suresh",
    name: "Suresh Yadav",
    rating: 4.5,
    jobs_completed: 178,
    image: "https://i.pravatar.cc/150?img=13",
    badge: "",
    area: "Prahlad Nagar",
  },
];

const FALLBACK_STATS = [
  { n: "100+", l: "Happy Ahmedabad homes" },
  { n: "20+", l: "Verified pros on board" },
  { n: "4.8★", l: "Average service rating" },
  { n: "15 min", l: "Avg. worker arrival ETA" },
];

const TRUST_BADGES = [
  {
    icon: Shield,
    t: "Background Verified",
    d: "Aadhaar + Field Officer on every pro",
  },
  {
    icon: BadgeCheck,
    t: "Zero Commission",
    d: "What you pay = what your pro earns. No hidden fees ever.",
  },
  // { icon: Timer,      t: 'On-Time Guarantee',   d: 'Late by 15 min? Your next clean is free.' },
  // { icon: Sparkles,   t: 'Re-clean Promise',    d: 'Not happy? We redo it at zero cost.' },
];

const FAQS = [
  {
    q: "Which areas of Ahmedabad do you cover?",
    a: "We serve all major Ahmedabad zones — Satellite, Bopal, SG Highway, Navrangpura, Vastrapur, Prahlad Nagar, Thaltej, Gota, Chandkheda, Naranpura, Paldi, CG Road, Maninagar and Ashram Road. Booking in 3 taps from any of these pincodes.",
  },
  {
    q: "Are your workers verified?",
    a: "Every GharSetGo pro goes through Aadhaar/PAN KYC, live selfie verification, police verification and a skill audit before being listed. Admin approves every single pro manually.",
  },
  {
    q: "How does AI matching work?",
    a: "Our AI Genie reads your request (even a plain sentence), considers worker skills, live location, rating, past jobs and ETA, and picks the single best pro for you. You can always override and pick manually.",
  },
  {
    q: "What are your service hours?",
    a: "GharSetGo accepts bookings between 7:00 am and 10:00 pm IST every single day. Outside this window the app politely holds your booking until 7 am so our pros get proper rest.",
  },
  {
    q: "Can I reschedule or cancel?",
    a: "Absolutely — free cancellation up to 30 minutes before the slot. Reschedules happen in a single tap from your Bookings page.",
  },
  {
    q: "What about payment?",
    a: "All payments are compulsory online via UPI on a secure QR code. No cash ever changes hands — safer for you and for our pros.",
  },
  {
    q: "Is there a satisfaction guarantee?",
    a: "Yes. Every pro on GharSetGo is KYC-verified, AI-matched and live-tracked. If a job is incomplete, our support team reviews the case end-to-end and works with you and the pro to make it right.",
  },
];

// V4 additions (Apr 2026)
const USP_DIFFERENTIATORS = [
  {
    icon: Rabbit,
    t: "On-demand. Hyperlocal. Fast.",
    d: "Like Uber for homes — tap, match, and a verified pro is at your door, not tomorrow, NOW. Median arrival under 15 minutes.",
  },
  {
    icon: Zap,
    t: "Instant match, not a directory",
    d: "Most apps hand you a list. We match you with ONE best-fit pro instantly based on skill, rating and live ETA.",
  },
  {
    icon: Gauge,
    t: "Zero commission · Honest pricing",
    d: "BHK-based transparent rates. What you pay your pro is what they earn — no platform cut, no hidden surcharges.",
  },
  {
    icon: Handshake,
    t: "Admin-mediated payments",
    d: "You pay GharSetGo via UPI QR. We pay our pros every single day. Cleaner, safer, no awkward cash moments.",
  },
];

const POLICIES = [
  { slug: "terms", title: "Terms of Service", icon: FileText },
  { slug: "privacy", title: "Privacy Policy", icon: Shield },
  { slug: "refund", title: "Refund Policy", icon: IndianRupee },
  { slug: "agreement", title: "User Agreement", icon: Handshake },
  { slug: "worker-terms", title: "Worker / Pro Agreement", icon: Users },
];

const POLICY_CONTENT = {
  terms: {
    title: "Terms of Service",
    updated: "Effective: 1 April 2026",
    body: [
      [
        "Welcome to GharSetGo",
        'GharSetGo ("we", "us") is an AI-first on-demand home-services platform operated in Ahmedabad, Gujarat. By creating an account, booking a service or registering as a pro, you agree to these Terms.',
      ],
      [
        "Eligibility",
        "You must be 18+ with a valid mobile number and home address in our serviceable zone. Pros must clear KYC, selfie verification and admin approval before taking any jobs.",
      ],
      [
        "Service Hours",
        "Bookings are accepted between 07:00–22:00 IST. No bookings are created between 22:00–07:00 to protect our pros' well-being.",
      ],
      [
        "Pricing",
        "Flat Rs.200 per standard service (60–80 min). Party/bulk-hour packages follow the quoted block price. All prices are inclusive of applicable taxes unless specified.",
      ],
      [
        "Payments",
        "All payments are compulsory online via UPI to GharSetGo's business UPI ID / QR. Pros do not accept cash. Attempting off-platform payment is a violation and will cause account suspension.",
      ],
      [
        "Cancellations",
        "Free cancellation up to 30 minutes before the slot. Late cancellations may attract a Rs.50 fee credited to the assigned pro's compensation.",
      ],
      [
        "Pro Verification",
        "Every pro clears Aadhaar/PAN KYC, a live selfie match, a police record check and a skill audit. Admin alone approves onboarding.",
      ],
      [
        "Limitation of Liability",
        "We are not liable for any of the damages. We are just a tech platform providing support.",
      ],
      ["Contact", "gharsetgo22@gmail.com"],
    ],
  },
  privacy: {
    title: "Privacy Policy",
    updated: "Effective: 1 April 2026",
    body: [
      [
        "Information We Collect",
        "Your name, mobile, email, home address, pincode and optional lat/lng; booking history; photos you upload (KYC, before/after); UPI ID for pros.",
      ],
      [
        "How We Use It",
        "To match you with the best pro, route them to your door, process UPI payments, compute pro payouts and power AI features like the Genie and Mess Scanner.",
      ],
      [
        "Sharing",
        "We share only the minimum needed — your name, address and contact with the assigned pro. We never sell personal data to third parties.",
      ],
      [
        "AI & Analytics",
        "We use GPT-5.2 and similar models to power matchmaking and suggestions. Your queries may be used anonymously to improve models.",
      ],
      [
        "Security",
        "Data is encrypted in transit (TLS) and at rest. Access is role-based and logged.",
      ],
      [
        "Your Rights",
        "You can export, correct or delete your data anytime from Settings → Data. GDPR/DPDP-style requests honored within 7 days.",
      ],
    ],
  },
  refund: {
    title: "Refund Policy",
    updated: "Effective: 1 April 2026",
    body: [
      [
        "Quality Promise",
        "Every pro on GharSetGo is KYC-verified, trained, AI-matched to your job, and rated after every visit. We do not promise free re-services or automatic refunds — instead, we invest in better matching, live tracking, and accountability so issues are rare in the first place.",
      ],
      [
        "Cancellations",
        'You can cancel before a pro is dispatched at no charge. Cancellations within the 30-minute "pro on the way" window may incur a partial fee, which is shared with the pro for their travel.',
      ],
      [
        "Disputes",
        "If you have a service dispute, raise it from the booking page within 24 hours of completion. Our team reviews video, photos, and pro logs to decide on a case-by-case basis. We do not auto-issue refunds.",
      ],
    ],
  },
  agreement: {
    title: "User Agreement",
    updated: "Effective: 1 April 2026",
    body: [
      [
        "Your Commitments as a User",
        "(1) Treat our pros with respect — abuse, harassment or discrimination means immediate account ban. (2) Provide accurate access to your home and basic safety (adequate lighting, clean water). (3) Pay online only — no cash, gifts or side deals.",
      ],
      [
        "The QR Start Flow",
        "At arrival, your pro scans the QR code / 6-digit OTP visible in your app. This is your consent signal that work may begin. Never share the OTP before the pro arrives at your door.",
      ],
      [
        "Property & Belongings",
        "Please store valuables safely before the service begins. GharSetGo is insured for damage up to Rs.5,000 per booking on proof of incident within 2 hours.",
      ],
    ],
  },
  "worker-terms": {
    title: "Worker / Pro Agreement",
    updated: "Effective: 1 April 2026",
    body: [
      [
        "Independent Contractor",
        "Pros on GharSetGo are independent service providers, not employees. You set your own online hours within the 7am–10pm window.",
      ],
      // ['Commission Split', 'Customers pay GharSetGo (the admin) 100% of the listed price via UPI. Pros earn 20% of the listed price per completed job, paid to their registered UPI ID every 24 hours.'],
      [
        "Mandatory KYC",
        "Aadhaar/PAN KYC + live selfie + admin approval are compulsory before any job.",
      ],
      [
        "QR Start Protocol",
        "You MUST scan the customer's QR / enter the 6-digit OTP on-site before starting work. Starting without verification is a code-of-conduct violation.",
      ],
      [
        "Code of Conduct",
        "Arrive on time. Be respectful. Use GharSetGo-supplied materials. Never accept cash or off-platform bookings.",
      ],
      [
        "Quality Bar",
        "Maintaining a 4.3★ rolling average is required. Three consecutive ratings ≤ 3★ triggers a coaching review.",
      ],
    ],
  },
};

// WhatsApp support chat — deep link with the customer message pre-filled
const WHATSAPP_URL = `https://wa.me/917990223450?text=${encodeURIComponent(
  "Hello, I'm a CUSTOMER",
)}`;

export default function LandingPage() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);
  const [showTop, setShowTop] = useState(false);
  const [services, setServices] = useState(FALLBACK_SERVICES);
  const [workers, setWorkers] = useState(FALLBACK_WORKERS);
  const [email, setEmail] = useState("");
  const [subscribed, setSubscribed] = useState(false);
  const [scanState, setScanState] = useState("before"); // before, scanning, after
  // AI Genie state
  const [genieInput, setGenieInput] = useState("");
  const [genieLoading, setGenieLoading] = useState(false);
  const [genieResult, setGenieResult] = useState(null); // {summary, suggestions, powered_by}
  const [genieError, setGenieError] = useState("");
  const [stats, setStats] = useState(FALLBACK_STATS);
  // FAQ state
  const [openFaq, setOpenFaq] = useState(0);
  // Policy modal
  const [policy, setPolicy] = useState(null); // slug
  // Service hours (cached from backend)
  const [hours, setHours] = useState({
    open_hour: 7,
    close_hour: 22,
    is_open: true,
  });

  /* Signed-in redirect — /home route disabled
  useEffect(() => {
    if (user) navigate("/home", { replace: true });
  }, [user, navigate]);
  */
  useEffect(() => {
    // Fetch live data; keep fallbacks if anything fails so the sections always render.
    api
      .get("/services")
      .then((r) => {
        if (Array.isArray(r.data) && r.data.length > 0) setServices(r.data);
      })
      .catch((err) => {
        console.warn(
          "[GharSetGo] Could not load services, using fallback:",
          err?.message,
        );
      });
    api
      .get("/workers")
      .then((r) => {
        if (Array.isArray(r.data) && r.data.length > 0) {
          setWorkers(r.data.sort((a, b) => (b.rating || 0) - (a.rating || 0)));
        }
      })
      .catch((err) => {
        console.warn(
          "[GharSetGo] Could not load workers, using fallback:",
          err?.message,
        );
      });
    api
      .get("/service-hours")
      .then((r) => {
        if (r.data) setHours(r.data);
      })
      .catch(() => { });
    api
      .get("/stats")
      .then((r) => {
        if (Array.isArray(r.data?.stats) && r.data.stats.length > 0) {
          setStats(r.data.stats);
        }
      })
      .catch(() => { });
  }, []);
  useEffect(() => {
    const fn = () => {
      setScrolled(window.scrollY > 60);
      setShowTop(window.scrollY > 600);
    };
    window.addEventListener("scroll", fn, { passive: true });
    return () => window.removeEventListener("scroll", fn);
  }, []);
  useEffect(() => {
    const obs = new IntersectionObserver(
      (entries) =>
        entries.forEach((e) => {
          if (e.isIntersecting) e.target.classList.add("scroll-visible");
        }),
      { threshold: 0.08, rootMargin: "0px 0px -30px 0px" },
    );
    document
      .querySelectorAll(".scroll-reveal")
      .forEach((el) => obs.observe(el));
    // FAILSAFE: on some browsers / iframed previews IntersectionObserver can stall.
    // Force-reveal everything after 1.5s so content is guaranteed to be visible.
    const t = setTimeout(() => {
      document
        .querySelectorAll(".scroll-reveal")
        .forEach((el) => el.classList.add("scroll-visible"));
    }, 1500);
    return () => {
      obs.disconnect();
      clearTimeout(t);
    };
  }, [services, workers]);

  const handleScan = useCallback(() => {
    if (scanState === "before") {
      setScanState("scanning");
      setTimeout(() => setScanState("after"), 2500);
    } else if (scanState === "after") {
      setScanState("before");
    }
  }, [scanState]);

  const handleGenie = useCallback(
    async (q) => {
      const query = (q ?? genieInput).trim();
      if (query.length < 5) {
        setGenieError("Tell me a little more about your home...");
        return;
      }
      setGenieError("");
      setGenieLoading(true);
      setGenieResult(null);
      try {
        const { data } = await api.post("/ai/suggest-services", { query });
        setGenieResult(data);
      } catch (e) {
        setGenieError("Our genie is taking a chai break. Try again in a sec.");
      } finally {
        setGenieLoading(false);
      }
    },
    [genieInput],
  );

  const fmtCount = (n) => (n >= 1000 ? (n / 1000).toFixed(1) + "k" : n);

  return (
    <div className="min-h-screen overflow-x-hidden">
      <div className="bg-pattern" />

      {/* NAV — bright over hero, glass-dark when scrolled */}
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${scrolled ? "py-3" : "py-5"}`}
        style={
          scrolled
            ? {
              background: "rgba(255,255,255,0.85)",
              backdropFilter: "blur(24px) saturate(1.5)",
              borderBottom: "1px solid rgba(108,76,241,0.08)",
            }
            : { background: "transparent" }
        }
        data-testid="landing-navbar"
      >
        <div className="max-w-[1200px] mx-auto px-6 flex items-center justify-between">
          <div className="flex flex-col items-start leading-tight">
            <Logo size={36} variant="full" />
            <span
              className="text-[10px] italic text-[#6C4CF1] mt-0.5 ml-0.5"
              data-testid="navbar-tagline"
            >
              Har kaam, bas set ho
            </span>
          </div>
          <div className="hidden md:flex items-center gap-6 text-[13px] font-medium text-[#4B5563]">
            <a href="#why" className="hover:text-[#6C4CF1] transition-colors">
              Why
            </a>
            {/* Packages anchor — target hidden
            <a
              href="#packages"
              className="hover:text-[#6C4CF1] transition-colors"
            >
              Packages
            </a>
            */}
            <a
              href="#services"
              className="hover:text-[#6C4CF1] transition-colors"
            >
              Services
            </a>
            <a href="#faq" className="hover:text-[#6C4CF1] transition-colors">
              FAQ
            </a>
          </div>
          <button
            data-testid="landing-get-started-btn"
            // onClick={login}
            className="rounded-full px-6 py-2.5 text-sm font-semibold text-white transition-all hover:-translate-y-0.5"
            style={{
              background: "linear-gradient(135deg, #6C4CF1, #8B5CF6)",
              boxShadow: "0 4px 16px rgba(108,76,241,0.35)",
            }}
          >
            Get Started →
          </button>
        </div>
      </nav>

      {/* HERO — Bright */}
      <section
        className="relative pt-24 sm:pt-28 pb-20 px-6 overflow-hidden"
        data-testid="hero-section"
        style={{
          background:
            "radial-gradient(circle at 50% 0%, #F3EEFF 0%, #FFFFFF 60%, #FFF8E1 100%)",
        }}
      >
        {/* Subtle dot grid */}
        <div
          className="absolute inset-0 opacity-[0.08] pointer-events-none"
          style={{
            backgroundImage:
              "radial-gradient(circle at 1px 1px, #6C4CF1 1px, transparent 0)",
            backgroundSize: "32px 32px",
          }}
        />
        <div className="absolute top-20 -left-20 w-80 h-80 bg-[#6C4CF1]/15 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute bottom-10 -right-20 w-80 h-80 bg-[#FFD84D]/25 rounded-full blur-3xl pointer-events-none" />

        <div className="max-w-[1200px] mx-auto relative">
          {/* Status bar */}
          <div className="flex items-center justify-center gap-2 mb-6 animate-fade-in">
            <span
              className="w-1.5 h-1.5 rounded-full bg-[#2EE6C9] animate-pulse"
              style={{ boxShadow: "0 0 8px #2EE6C9" }}
            />
            <span className="text-[9px] font-mono tracking-[0.25em] uppercase text-[#4B5563]">
              Live · Serving Ahmedabad · 100+ Homes
            </span>
          </div>

          <div className="text-center mb-10 animate-fade-in-up">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full mb-6 bg-white border border-[#6C4CF1]/20 shadow-sm">
              <Zap className="w-3.5 h-3.5 text-[#6C4CF1]" strokeWidth={2} />
              <span className="text-[10px] font-bold text-[#6C4CF1] tracking-[0.2em] uppercase">
                Har Kaam, Bas Set Ho
              </span>
            </div>
            <h1
              className="text-5xl sm:text-6xl lg:text-7xl font-bold text-[#0A0A0A] leading-[1.04] tracking-tight mb-5"
              style={{ fontFamily: "Poppins" }}
            >
              Your Home.
              <br />
              <span
                className="bg-clip-text text-transparent"
                style={{
                  backgroundImage:
                    "linear-gradient(135deg, #6C4CF1 0%, #FFC93C 100%)",
                }}
              >
                Upgraded.
              </span>
            </h1>
            <p className="text-base sm:text-lg text-[#4B5563] max-w-xl mx-auto mb-8 leading-relaxed">
              AI-powered home services. Live cleanliness scores, verified pros,
              three-tap booking — finally, a home that runs itself.
            </p>
            <div
              className="flex flex-wrap justify-center gap-3"
              data-testid="hero-cta-row"
            >
              <button
                data-testid="hero-get-started-btn"
                // onClick={() => navigate("/login")}
                className="relative overflow-hidden rounded-full px-8 py-3.5 text-sm font-semibold text-white transition-all hover:-translate-y-0.5 group"
                style={{
                  background: "linear-gradient(135deg, #6C4CF1, #8B5CF6)",
                  boxShadow: "0 8px 32px rgba(108,76,241,0.4)",
                }}
              >
                <span className="relative z-10 flex items-center gap-2">
                  Book Your Pro{" "}
                  <ArrowRight
                    className="w-4 h-4 group-hover:translate-x-1 transition-transform"
                    strokeWidth={2}
                  />
                </span>
              </button>
              {/* Join as Pro — worker signup hidden
              <button
                data-testid="hero-worker-cta"
                onClick={() => navigate("/worker/login")}
                className="rounded-full px-8 py-3.5 text-sm font-semibold text-[#4B5563] bg-white border border-[#E5E7EB] hover:border-[#6C4CF1] hover:text-[#6C4CF1] transition-all hover:-translate-y-0.5"
              >
                Join as Pro
              </button>
              */}
            </div>
          </div>

          {/* Hero photo — 3 cleaners (transparent PNG, sits on bright gradient) */}
          <div
            className="relative max-w-3xl mx-auto animate-fade-in-up stagger-2"
            data-testid="hero-team-photo"
          >
            <div className="absolute inset-x-10 bottom-0 h-32 bg-gradient-to-t from-[#6C4CF1]/10 to-transparent rounded-full blur-2xl pointer-events-none" />
            <img
              src="/images/hero_image.png"
              alt="GharSetGo pros — verified, trained & ready"
              className="relative w-full h-auto object-contain drop-shadow-2xl"
              style={{ maxHeight: "520px" }}
              loading="eager"
            />
            {/* Floating trust pills */}
            <div className="absolute top-4 left-2 sm:left-6 px-3 py-1.5 rounded-full bg-white border border-[#E5E7EB] shadow-md flex items-center gap-1.5 animate-fade-in-up stagger-3">
              <Shield className="w-3 h-3 text-[#2EE6C9]" />
              <span className="text-[10px] font-bold text-[#0A0A0A]">
                KYC Verified
              </span>
            </div>
            <div className="absolute top-4 right-2 sm:right-6 px-3 py-1.5 rounded-full bg-white border border-[#E5E7EB] shadow-md flex items-center gap-1.5 animate-fade-in-up stagger-4">
              <Star className="w-3 h-3 text-[#FFC93C] fill-[#FFC93C]" />
              <span className="text-[10px] font-bold text-[#0A0A0A]">
                4.7 / 5 · Trusted local service
              </span>
            </div>
          </div>

          <p className="text-center text-[10px] font-mono tracking-[0.22em] uppercase text-[#9CA3AF] mt-6">
            ↓ Scroll to explore ↓
          </p>
        </div>
      </section>

      {/* STATS STRIP — dark→light transition */}
      <section className="relative px-6 pb-4 -mt-8" data-testid="stats-strip">
        <div className="max-w-[1200px] mx-auto relative z-10">
          <div className="glass rounded-[20px] grid grid-cols-2 sm:grid-cols-4 divide-x divide-[#E5E7EB]/70 overflow-hidden shadow-xl">
            {stats.map((s) => (
              <div key={s.l} className="py-4 sm:py-5 px-3 text-center">
                <div
                  className="text-xl sm:text-2xl font-bold gradient-text"
                  style={{ fontFamily: "Poppins" }}
                >
                  {s.n}
                </div>
                <div className="text-[10px] sm:text-xs text-[#4B5563] mt-0.5">
                  {s.l}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SERVICE HOURS STRIP */}
      <section className="px-6 pb-2" data-testid="service-hours-strip">
        <div className="max-w-[1200px] mx-auto">
          <div
            className={`rounded-[16px] px-4 py-3 flex items-center justify-center gap-3 text-xs ${hours.is_open ? "bg-[#E8FFF5] text-[#065F46]" : "bg-[#FEF3C7] text-[#92400E]"}`}
          >
            {hours.is_open ? (
              <Sun className="w-4 h-4" />
            ) : (
              <Moon className="w-4 h-4" />
            )}
            <span className="font-semibold">
              {hours.is_open
                ? "We're LIVE right now · "
                : "We're resting right now · "}
              GharSetGo serves homes from <b>7:00 am to 10:00 pm IST</b> daily.
            </span>
          </div>
        </div>
      </section>

      {/* SMART HOME MAP — signature feature spotlight */}
      <section
        className="py-12 sm:py-20 px-6"
        data-testid="smart-home-map-section"
      >
        <div className="max-w-[1200px] mx-auto">
          <div className="text-center mb-8">
            <span className="text-xs font-bold tracking-[0.22em] uppercase text-[#6C4CF1] mb-2 block">
              Signature Feature
            </span>
            <h2
              className="text-3xl sm:text-4xl font-bold text-[#0A0A0A]"
              style={{ fontFamily: "Poppins" }}
            >
              Your home, visualised.
            </h2>
            <p className="text-sm sm:text-base text-[#4B5563] mt-3 max-w-2xl mx-auto">
              Live cleanliness scores for every room, AI nudges when something's
              slipping, and one-tap booking — all from a smart floor map only
              GharSetGo gives you.
            </p>
          </div>
          <div
            className="relative max-w-5xl mx-auto rounded-[24px] overflow-hidden shadow-2xl border border-[#E5E7EB]"
            data-testid="smart-home-map-visual"
          >
            <div className="absolute -top-16 -left-16 w-64 h-64 bg-[#6C4CF1]/15 rounded-full blur-3xl pointer-events-none" />
            <div className="absolute -bottom-16 -right-16 w-64 h-64 bg-[#FFD84D]/20 rounded-full blur-3xl pointer-events-none" />
            <img
              src="https://customer-assets.emergentagent.com/job_3cfbfaa3-6b26-4d2c-bbad-bcc8c90ef543/artifacts/2nybic8i_Screenshot%202026-05-15%20at%201.51.31%E2%80%AFAM.png"
              alt="GharSetGo Smart Home Map — live cleanliness scores, ratings, ETA, pros count"
              className="relative w-full h-auto"
              loading="lazy"
            />
          </div>
          <div className="text-center mt-6">
            <button
              // onClick={() => navigate("/login")}
              data-testid="smart-map-cta"
              className="inline-flex items-center gap-2 rounded-full px-7 py-3 text-sm font-semibold text-white transition-all hover:-translate-y-0.5"
              style={{
                background: "linear-gradient(135deg, #6C4CF1, #8B5CF6)",
                boxShadow: "0 8px 24px rgba(108,76,241,0.35)",
              }}
            >
              Try Smart Home Map <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </section>

      {/* WHY WE'RE DIFFERENT / USP */}
      <section
        id="why"
        className="py-12 sm:py-16 px-6"
        data-testid="usp-section"
      >
        <div className="max-w-[1200px] mx-auto">
          <div className="text-center mb-8">
            <span className="text-xs font-bold tracking-[0.2em] uppercase text-[#8B5CF6] mb-2 block">
              Why GharSetGo?
            </span>
            <h2
              className="text-2xl sm:text-3xl font-bold text-[#0A0A0A]"
              style={{ fontFamily: "Poppins" }}
            >
              Not another listing app. A true on-demand engine.
            </h2>
            <p className="text-sm text-[#4B5563] mt-2 max-w-2xl mx-auto">
              We built GharSetGo the way Uber/Ola built ride-hailing —{" "}
              <b>hyperlocal, instant, and AI-matched</b>. No waiting 2 days for
              a quote.
            </p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
            {USP_DIFFERENTIATORS.map((u, i) => (
              <div
                key={u.t}
                className="glass rounded-[18px] p-5 card-hover animate-fade-in-up"
                style={{ animationDelay: `${i * 80}ms` }}
                data-testid={`usp-${i}`}
              >
                <div className="w-10 h-10 rounded-[12px] bg-gradient-to-br from-[#6C4CF1] to-[#8B5CF6] flex items-center justify-center mb-3">
                  <u.icon className="w-5 h-5 text-white" />
                </div>
                <h4
                  className="text-sm font-bold text-[#0A0A0A] mb-1"
                  style={{ fontFamily: "Poppins" }}
                >
                  {u.t}
                </h4>
                <p className="text-[11px] text-[#4B5563] leading-relaxed">
                  {u.d}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* AI GENIE — talk to us in one sentence */}
      <section
        id="genie"
        className="py-12 sm:py-16 px-6"
        data-testid="genie-section"
      >
        <div className="max-w-[1100px] mx-auto">
          <div
            className="rounded-[28px] relative overflow-hidden"
            style={{
              background:
                "linear-gradient(135deg, #1E0B4C 0%, #3B1E8F 40%, #6C4CF1 100%)",
            }}
          >
            <div className="absolute -top-16 -right-10 w-64 h-64 bg-[#FFD84D]/15 rounded-full blur-3xl pointer-events-none" />
            <div className="absolute -bottom-16 -left-10 w-64 h-64 bg-[#2EE6C9]/15 rounded-full blur-3xl pointer-events-none" />
            <div
              className="absolute inset-0 opacity-[0.08] pointer-events-none"
              style={{
                backgroundImage:
                  "radial-gradient(circle at 2px 2px, rgba(255,255,255,0.5) 1px, transparent 0)",
                backgroundSize: "22px 22px",
              }}
            />
            <div className="relative z-10 p-6 sm:p-10">
              <div className="text-center mb-6">
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/10 mb-4 border border-white/15">
                  <Wand2 className="w-3.5 h-3.5 text-[#FFD84D]" />
                  <span className="text-[10px] font-bold text-white tracking-[0.18em] uppercase">
                    AI Genie · Powered by GPT-5
                  </span>
                </div>
                <h2
                  className="text-2xl sm:text-4xl font-bold text-white mb-2"
                  style={{ fontFamily: "Poppins" }}
                >
                  Describe your home.{" "}
                  <span className="text-[#FFD84D]">We'll do the rest.</span>
                </h2>
                <p className="text-white/70 text-sm max-w-xl mx-auto">
                  Tell our AI Genie what's up at home in plain English (or
                  Hinglish 😉) and get instant, tailored service picks.
                </p>
              </div>

              {/* Input pill */}
              <div className="max-w-2xl mx-auto">
                <div
                  className="flex flex-col sm:flex-row gap-2 p-2 rounded-[22px] bg-white/10 border border-white/20 backdrop-blur-md"
                  data-testid="genie-input-wrap"
                >
                  <div className="flex items-center gap-2 flex-1 px-3">
                    <MessageSquare className="w-4 h-4 text-white/50 shrink-0" />
                    <input
                      type="text"
                      value={genieInput}
                      onChange={(e) => setGenieInput(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") handleGenie();
                      }}
                      placeholder="e.g. kitchen's oily, lizard in the bathroom..."
                      className="flex-1 bg-transparent outline-none text-white text-sm placeholder-white/40 py-3"
                      data-testid="genie-input"
                    />
                  </div>
                  <button
                    onClick={() => handleGenie()}
                    disabled={genieLoading}
                    className="bg-[#FFD84D] text-[#0A0A0A] font-bold text-sm rounded-[16px] px-5 py-3 inline-flex items-center justify-center gap-2 hover:bg-[#FFE066] transition-colors disabled:opacity-60"
                    data-testid="genie-submit-btn"
                  >
                    {genieLoading ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Thinking…
                      </>
                    ) : (
                      <>
                        <Wand2 className="w-4 h-4" />
                        Ask Genie
                      </>
                    )}
                  </button>
                </div>
                {genieError && (
                  <p
                    className="text-[#FFD84D] text-xs mt-2 text-center"
                    data-testid="genie-error"
                  >
                    {genieError}
                  </p>
                )}

                {/* Prompt chips */}
                <div className="flex flex-wrap justify-center gap-2 mt-4">
                  {GENIE_PROMPTS.map((p, i) => (
                    <button
                      key={i}
                      onClick={() => {
                        setGenieInput(p);
                        handleGenie(p);
                      }}
                      className="text-[11px] px-3 py-1.5 rounded-full bg-white/5 border border-white/15 text-white/70 hover:bg-white/10 hover:text-white transition-colors"
                      data-testid={`genie-chip-${i}`}
                    >
                      {p}
                    </button>
                  ))}
                </div>
              </div>

              {/* Result */}
              {genieResult && (
                <div
                  className="mt-8 max-w-3xl mx-auto animate-fade-in-up"
                  data-testid="genie-result"
                >
                  <div className="flex items-start gap-3 mb-4">
                    <div className="w-8 h-8 rounded-full bg-[#FFD84D] flex items-center justify-center shrink-0">
                      <Wand2 className="w-4 h-4 text-[#0A0A0A]" />
                    </div>
                    <div className="glass-strong rounded-[16px] p-3 text-sm text-[#0A0A0A]">
                      {genieResult.summary}
                    </div>
                  </div>
                  <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                    {genieResult.suggestions.map((sug, i) => {
                      const svc = services.find(
                        (s) => s.service_id === sug.service_id,
                      );
                      const Icon = ICON_MAP[svc?.icon] || Sparkles;
                      const urgencyColor =
                        sug.urgency === "high"
                          ? "#FF7A59"
                          : sug.urgency === "low"
                            ? "#9CA3AF"
                            : "#FFC93C";
                      return (
                        <div
                          key={sug.service_id + i}
                          className="bg-white rounded-[18px] p-4 shadow-lg flex flex-col gap-2"
                          data-testid={`genie-suggestion-${i}`}
                        >
                          <div className="flex items-start justify-between gap-2">
                            <div className="flex items-center gap-2">
                              <div
                                className="w-9 h-9 rounded-[10px] flex items-center justify-center"
                                style={{ background: TINT_COLORS[i] }}
                              >
                                <Icon
                                  className="w-4 h-4"
                                  style={{ color: ACCENT_COLORS[i] }}
                                />
                              </div>
                              <div>
                                <h4 className="text-sm font-bold text-[#0A0A0A] leading-tight">
                                  {sug.name}
                                </h4>
                                <div className="flex items-center gap-1.5 text-[10px] text-[#9CA3AF]">
                                  <IndianRupee className="w-2.5 h-2.5" />
                                  {sug.price}
                                  <span>·</span>
                                  <Clock className="w-2.5 h-2.5" />
                                  {sug.duration}m
                                </div>
                              </div>
                            </div>
                            <span
                              className="text-[9px] font-bold px-2 py-0.5 rounded-full text-white shrink-0"
                              style={{ background: urgencyColor }}
                            >
                              {sug.urgency.toUpperCase()}
                            </span>
                          </div>
                          <p className="text-xs text-[#4B5563] leading-relaxed line-clamp-3">
                            {sug.reason}
                          </p>
                          <div className="flex items-center justify-between pt-1 mt-auto">
                            <div className="flex items-center gap-1 text-[10px] text-[#6C4CF1] font-semibold">
                              <Sparkles className="w-3 h-3" />
                              {sug.confidence}% match
                            </div>
                            <button
                              onClick={login}
                              className="text-[11px] font-bold bg-[#0A0A0A] text-white rounded-full px-3 py-1.5 hover:bg-[#1a1a2e] transition-colors"
                              data-testid={`genie-book-btn-${i}`}
                            >
                              Book <ArrowRight className="w-3 h-3 inline" />
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  <p className="text-[10px] text-white/50 text-center mt-4">
                    {genieResult.powered_by === "ai"
                      ? "✨ AI picked these for you"
                      : "Smart-matched from your description"}
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* TRUST BADGES */}
      <section className="px-6 pb-2" data-testid="trust-badges">
        <div className="max-w-[1200px] mx-auto flex flex-wrap justify-center gap-3">
          {TRUST_BADGES.map((b) => (
            <div
              key={b.t}
              className="glass rounded-[16px] p-4 flex items-start gap-3 w-full sm:w-[calc(50%-0.375rem)] lg:w-[280px]"
            >
              <div className="w-9 h-9 rounded-[10px] bg-[#F3EEFF] flex items-center justify-center shrink-0">
                <b.icon className="w-4 h-4 text-[#6C4CF1]" />
              </div>
              <div>
                <h4 className="text-xs font-bold text-[#0A0A0A] leading-tight">
                  {b.t}
                </h4>
                <p className="text-[10px] text-[#4B5563] mt-0.5 leading-snug">
                  {b.d}
                </p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* AD SLOT — sponsored placement (placeholder until AdSense ID set) */}
      {/* <section className="py-6 px-6" data-testid="landing-ad-section">
        <div className="max-w-[1200px] mx-auto">
          <AdSlot slot="" label="Sponsored" testId="landing-ad-1" />
        </div>
      </section> */}

      {/* PACKAGES — BHK-tiered pricing (hidden)
      <section
        id="packages"
        className="py-12 sm:py-16 px-6"
        data-testid="packages-section"
      >
        <div className="max-w-[1200px] mx-auto">
          <div className="text-center mb-8">
            <span className="text-xs font-bold tracking-[0.2em] uppercase text-[#8B5CF6] mb-2 block">
              Packages
            </span>
            <h2
              className="text-2xl sm:text-3xl font-bold text-[#0A0A0A]"
              style={{ fontFamily: "Poppins" }}
            >
              Pricing by home size
            </h2>
            <p className="text-sm text-[#4B5563] mt-2 max-w-2xl mx-auto">
              Pay for your home, not for someone else's. Transparent BHK-tier
              pricing for our most popular services.
            </p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {services
              .filter((s) => s.size_based && s.bhk_pricing)
              .map((svc, idx) => {
                const Icon = ICON_MAP[svc.icon] || Sparkles;
                const isCombo = svc.is_bundle;
                return (
                  <div
                    key={svc.service_id}
                    onClick={login}
                    data-testid={`package-${svc.service_id}`}
                    className={`group relative rounded-[20px] overflow-hidden card-hover cursor-pointer animate-fade-in-up ${isCombo ? "ring-2 ring-[#FFD84D]" : ""}`}
                    style={{
                      animationDelay: `${idx * 70}ms`,
                      background: isCombo
                        ? "linear-gradient(135deg, #2D1B69, #6C4CF1)"
                        : "white",
                      border: isCombo ? "none" : "1px solid #E5E7EB",
                    }}
                  >
                    {isCombo && (
                      <span className="absolute top-3 right-3 text-[9px] font-bold px-2 py-1 rounded-full bg-[#FFD84D] text-[#0A0A0A] z-10">
                        BEST VALUE
                      </span>
                    )}
                    <div className="p-5">
                      <div className="flex items-center gap-2 mb-3">
                        <div
                          className={`w-10 h-10 rounded-[12px] flex items-center justify-center ${isCombo ? "bg-white/15" : "bg-[#F3EEFF]"}`}
                        >
                          <Icon
                            className={`w-5 h-5 ${isCombo ? "text-[#FFD84D]" : "text-[#6C4CF1]"}`}
                          />
                        </div>
                        <h4
                          className={`font-bold text-sm ${isCombo ? "text-white" : "text-[#0A0A0A]"}`}
                          style={{ fontFamily: "Poppins" }}
                        >
                          {svc.name}
                        </h4>
                      </div>
                      <p
                        className={`text-[10px] mb-4 leading-relaxed line-clamp-2 ${isCombo ? "text-white/70" : "text-[#4B5563]"}`}
                      >
                        {svc.description}
                      </p>
                      <div className="space-y-1.5">
                        {Object.entries(svc.bhk_pricing).map(
                          ([size, price]) => (
                            <div
                              key={size}
                              className={`flex items-center justify-between py-1.5 px-2.5 rounded-[8px] ${isCombo ? "bg-white/10" : "bg-[#F9FAFB]"}`}
                            >
                              <span
                                className={`text-[11px] font-semibold ${isCombo ? "text-white/90" : "text-[#4B5563]"}`}
                              >
                                {size}
                              </span>
                              <span
                                className={`text-sm font-bold ${isCombo ? "text-[#FFD84D]" : "text-[#6C4CF1]"}`}
                              >
                                Rs. {price}
                              </span>
                            </div>
                          ),
                        )}
                      </div>
                      <div
                        className={`mt-3 pt-3 border-t flex items-center justify-between ${isCombo ? "border-white/20" : "border-[#E5E7EB]"}`}
                      >
                        <span
                          className={`text-[10px] flex items-center gap-1 ${isCombo ? "text-white/70" : "text-[#9CA3AF]"}`}
                        >
                          <Clock className="w-3 h-3" />
                          {svc.duration} min
                        </span>
                        <span
                          className={`text-[10px] font-bold flex items-center gap-1 ${isCombo ? "text-[#FFD84D]" : "text-[#0A0A0A]"}`}
                        >
                          Book <ArrowRight className="w-3 h-3" />
                        </span>
                      </div>
                    </div>
                  </div>
                );
              })}
          </div>
        </div>
      </section>
      */}

      {/* SERVICES - All core services, 4-col grid */}
      <section
        id="services"
        className="py-12 sm:py-16 px-6"
        data-testid="services-section"
      >
        <div className="max-w-[1200px] mx-auto">
          <div className="text-center mb-8">
            <span className="text-xs font-bold tracking-[0.2em] uppercase text-[#8B5CF6] mb-2 block">
              Our Services
            </span>
            <h2
              className="text-2xl sm:text-3xl font-bold text-[#0A0A0A]"
              style={{ fontFamily: "Poppins" }}
            >
              You pay exactly what you ask for
            </h2>
            <p className="text-sm text-[#4B5563] mt-2">
              Transparent BHK-based rates. What you pay is what your pro earns.
            </p>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
            {services
              .filter(
                (s) => !s.tier || s.tier === "standard" || s.tier === "premium",
              )
              .map((svc, idx) => {
                const Icon = ICON_MAP[svc.icon] || Sparkles;
                // const minPrice = svc.bhk_pricing
                //   ? Math.min(...Object.values(svc.bhk_pricing))
                //   : svc.price;
                // const maxPrice = svc.bhk_pricing
                //   ? Math.max(...Object.values(svc.bhk_pricing))
                //   : svc.price;
                // const priceLabel =
                //   svc.size_based && minPrice !== maxPrice
                //     ? `Rs.${minPrice} – ${maxPrice}`
                //     : `Rs.${svc.price}`;
                return (
                  <div
                    key={svc.service_id}
                    className="group cursor-pointer card-hover animate-fade-in-up"
                    style={{ animationDelay: `${Math.min(idx * 60, 400)}ms` }}
                    onClick={login}
                    data-testid={`landing-service-${idx}`}
                  >
                    <div className="glass rounded-[16px] overflow-hidden">
                      <div
                        className="aspect-[4/3] overflow-hidden flex items-center justify-center"
                        style={{
                          background: TINT_COLORS[idx] || TINT_COLORS[0],
                        }}
                      >
                        {svc.image ? (
                          <img
                            src={svc.image}
                            alt={svc.name}
                            loading="lazy"
                            className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-110"
                          />
                        ) : (
                          <Icon
                            className="w-12 h-12 sm:w-14 sm:h-14 transition-transform duration-300 group-hover:scale-110"
                            style={{ color: ACCENT_COLORS[idx] || "#6C4CF1" }}
                          />
                        )}
                      </div>
                      <div className="p-3">
                        <h4 className="font-semibold text-[#0A0A0A] text-xs mb-0.5 line-clamp-1">
                          {svc.name}
                        </h4>
                        <p className="text-[9px] text-[#9CA3AF] line-clamp-1 mb-1">
                          {svc.description}
                        </p>
                        <div className="flex items-center gap-2 mb-1">
                          <span className="flex items-center gap-0.5">
                            <Star className="w-3 h-3 text-[#FFC93C] fill-[#FFC93C]" />
                            <span className="text-[10px] font-bold">
                              {svc.avg_rating || "4.5"}
                            </span>
                          </span>
                          <span className="text-[9px] text-[#9CA3AF]">
                            {fmtCount(svc.booking_count || 100)} booked
                          </span>
                        </div>
                        <div className="flex items-center justify-end">
                          {/* <span className="text-xs font-bold text-[#6C4CF1]">
                            {priceLabel}
                          </span> */}
                          <span className="text-[9px] text-[#9CA3AF] flex items-center gap-0.5">
                            <Clock className="w-2.5 h-2.5" />
                            {svc.duration}m
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
          </div>
        </div>
      </section>

      {/* NICHE SERVICES — hyperlocal differentiators */}
      {services.filter((s) => s.tier === "niche").length > 0 && (
        <section
          id="niche"
          className="py-12 sm:py-16 px-6"
          data-testid="niche-section"
        >
          <div className="max-w-[1200px] mx-auto">
            <div className="text-center mb-8">
              <span className="text-xs font-bold tracking-[0.2em] uppercase text-[#FF7A59] mb-2 block">
                Niche · Only on GharSetGo
              </span>
              <h2
                className="text-2xl sm:text-3xl font-bold text-[#0A0A0A]"
                style={{ fontFamily: "Poppins" }}
              >
                Services you won't find anywhere else
              </h2>
              <p className="text-sm text-[#4B5563] mt-2 max-w-2xl mx-auto">
                Built for Ahmedabad homes. Because your Diwali, your party and
                your pet all deserve specialised care.
              </p>
            </div>
            <div className="grid sm:grid-cols-3 gap-4">
              {services
                .filter((s) => s.tier === "niche")
                .map((svc, idx) => {
                  const Icon = ICON_MAP[svc.icon] || Sparkles;
                  return (
                    <div
                      key={svc.service_id}
                      className="relative glass rounded-[20px] overflow-hidden card-hover cursor-pointer animate-fade-in-up"
                      style={{ animationDelay: `${idx * 80}ms` }}
                      onClick={login}
                      data-testid={`niche-service-${idx}`}
                    >
                      <div className="absolute top-3 right-3 z-10">
                        <span className="text-[9px] font-bold px-2 py-1 rounded-full bg-[#FF7A59] text-white shadow">
                          NICHE
                        </span>
                      </div>
                      <div
                        className="aspect-[16/9] flex items-center justify-center relative"
                        style={{
                          background: `linear-gradient(135deg, ${ACCENT_COLORS[idx % ACCENT_COLORS.length]}30, #FFD84D20)`,
                        }}
                      >
                        <Icon
                          className="w-14 h-14"
                          style={{
                            color: ACCENT_COLORS[idx % ACCENT_COLORS.length],
                          }}
                        />
                      </div>
                      <div className="p-4">
                        <h4
                          className="font-bold text-[#0A0A0A] text-sm mb-1"
                          style={{ fontFamily: "Poppins" }}
                        >
                          {svc.name}
                        </h4>
                        <p className="text-[11px] text-[#4B5563] mb-2 leading-relaxed line-clamp-2">
                          {svc.description}
                        </p>
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-bold text-[#6C4CF1]">
                            Rs. {svc.price}
                          </span>
                          <span className="text-[10px] text-[#9CA3AF] flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            {svc.duration} min
                          </span>
                        </div>
                      </div>
                    </div>
                  );
                })}
            </div>
          </div>
        </section>
      )}

      {/* PARTY / BULK HOURS */}
      {services.filter((s) => s.tier === "bulk").length > 0 && (
        <section
          id="party"
          className="py-12 sm:py-16 px-6"
          data-testid="party-section"
        >
          <div className="max-w-[1200px] mx-auto">
            <div
              className="rounded-[28px] relative overflow-hidden"
              style={{
                background:
                  "linear-gradient(135deg, #2D1B69 0%, #6C4CF1 55%, #8B5CF6 100%)",
              }}
            >
              <div className="absolute -top-20 -right-16 w-72 h-72 bg-[#FFD84D]/20 rounded-full blur-3xl pointer-events-none" />
              <div className="absolute -bottom-20 -left-16 w-72 h-72 bg-[#FFD84D]/15 rounded-full blur-3xl pointer-events-none" />
              <div
                className="absolute inset-0 opacity-[0.08] pointer-events-none"
                style={{
                  backgroundImage:
                    "radial-gradient(circle at 2px 2px, rgba(255,255,255,0.5) 1px, transparent 0)",
                  backgroundSize: "22px 22px",
                }}
              />
              <div className="relative z-10 p-6 sm:p-10">
                <div className="text-center mb-6">
                  <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#FFD84D]/15 mb-3 border border-[#FFD84D]/30">
                    <PartyPopper className="w-3.5 h-3.5 text-[#FFD84D]" />
                    <span className="text-[10px] font-bold text-[#FFD84D] tracking-[0.18em] uppercase">
                      Party · Events · Bulk Hours
                    </span>
                  </div>
                  <h2
                    className="text-2xl sm:text-4xl font-bold text-white mb-2"
                    style={{ fontFamily: "Poppins" }}
                  >
                    Throwing a party?{" "}
                    <span className="text-[#FFD84D]">
                      We'll handle the rest.
                    </span>
                  </h2>
                  <p className="text-white/80 text-sm max-w-xl mx-auto">
                    Book a crew of pros for 3, 6 or 8 hours straight. Serving,
                    kitchen, clean-up — before, during and after your event.
                  </p>
                </div>
                <div className="grid sm:grid-cols-3 gap-3">
                  {services
                    .filter((s) => s.tier === "bulk")
                    .map((svc, idx) => (
                      <button
                        key={svc.service_id}
                        onClick={login}
                        data-testid={`party-${idx}`}
                        className="bg-white/95 backdrop-blur rounded-[18px] p-5 text-left hover:bg-white transition-all card-hover"
                      >
                        <div className="flex items-center gap-2 mb-3">
                          <div className="w-9 h-9 rounded-[10px] bg-[#F3EEFF] flex items-center justify-center">
                            <PartyPopper className="w-4 h-4 text-[#6C4CF1]" />
                          </div>
                          <span className="text-[9px] font-bold px-2 py-0.5 rounded-full bg-[#FFD84D] text-[#0A0A0A]">
                            {svc.duration / 60} HOURS
                          </span>
                        </div>
                        <h4
                          className="font-bold text-[#0A0A0A] text-sm mb-1"
                          style={{ fontFamily: "Poppins" }}
                        >
                          {svc.name}
                        </h4>
                        <p className="text-[11px] text-[#4B5563] mb-3 leading-relaxed">
                          {svc.description}
                        </p>
                        <div className="flex items-center justify-between">
                          <span className="text-lg font-bold text-[#6C4CF1]">
                            Rs. {svc.price}
                          </span>
                          <span className="text-[10px] text-[#0A0A0A] font-bold flex items-center gap-1">
                            Book <ArrowRight className="w-3 h-3" />
                          </span>
                        </div>
                      </button>
                    ))}
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* BEFORE / AFTER SCANNER - Click to scan */}
      <section
        id="scanner"
        className="py-12 sm:py-16 px-6"
        data-testid="scanner-section"
      >
        <div className="max-w-[1200px] mx-auto">
          <div className="text-center mb-8 scroll-reveal">
            <span className="text-xs font-bold tracking-[0.2em] uppercase text-[#8B5CF6] mb-2 block">
              AI Scanner
            </span>
            <h2
              className="text-2xl sm:text-3xl font-bold text-[#0A0A0A]"
              style={{ fontFamily: "Poppins" }}
            >
              See the Difference
            </h2>
            <p className="text-sm text-[#4B5563] mt-2">
              Click "Scan Now" to watch AI transform chaos into order
            </p>
          </div>
          <div className="max-w-3xl mx-auto scroll-reveal">
            <div className="grid sm:grid-cols-2 gap-4">
              {/* Before */}
              <div className="glass rounded-[20px] overflow-hidden">
                <div className="relative aspect-square overflow-hidden">
                  <img
                    src={BEFORE_IMG}
                    alt="Before"
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute top-3 left-3">
                    <span className="text-[10px] font-bold px-3 py-1 rounded-full bg-[#FF7A59] text-white">
                      BEFORE
                    </span>
                  </div>
                  <div className="absolute bottom-3 left-3">
                    <p className="text-white font-bold text-sm drop-shadow-lg">
                      Messy Room
                    </p>
                    <p className="text-white/70 text-[10px] drop-shadow-lg">
                      AI Score: 32/100
                    </p>
                  </div>
                </div>
              </div>
              {/* After / Scan */}
              <div className="glass rounded-[20px] overflow-hidden relative">
                <div className="relative aspect-square overflow-hidden">
                  {scanState === "before" ? (
                    <div
                      className="w-full h-full flex flex-col items-center justify-center bg-gradient-to-br from-[#2D1B69] to-[#6C4CF1] cursor-pointer"
                      onClick={handleScan}
                      data-testid="scan-trigger"
                    >
                      <div className="w-16 h-16 rounded-full bg-white/15 flex items-center justify-center mb-4 animate-pulse-glow">
                        <ScanLine className="w-8 h-8 text-[#FFD84D]" />
                      </div>
                      <p className="text-white font-bold text-base mb-1">
                        Scan Now
                      </p>
                      <p className="text-white/50 text-xs">
                        Click to start AI analysis
                      </p>
                    </div>
                  ) : scanState === "scanning" ? (
                    <div className="w-full h-full relative">
                      <img
                        src={BEFORE_IMG}
                        alt="Scanning"
                        className="w-full h-full object-cover"
                      />
                      <div className="absolute inset-0 bg-[#6C4CF1]/30" />
                      <div className="scan-line" />
                      <div className="absolute inset-0 flex flex-col items-center justify-center">
                        <div className="w-14 h-14 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center mb-3">
                          <ScanLine className="w-7 h-7 text-white animate-spin" />
                        </div>
                        <p className="text-white font-bold text-sm">
                          Analyzing...
                        </p>
                        <p className="text-white/60 text-[10px]">
                          AI is processing
                        </p>
                      </div>
                    </div>
                  ) : (
                    <div
                      className="w-full h-full relative cursor-pointer"
                      onClick={handleScan}
                    >
                      <img
                        src={AFTER_IMG}
                        alt="After"
                        className="w-full h-full object-cover animate-fade-in"
                      />
                      <div className="absolute top-3 left-3">
                        <span className="text-[10px] font-bold px-3 py-1 rounded-full bg-[#2EE6C9] text-white">
                          AFTER
                        </span>
                      </div>
                      <div className="absolute bottom-3 left-3">
                        <p className="text-white font-bold text-sm drop-shadow-lg">
                          Clean & Organized!
                        </p>
                        <p className="text-white/70 text-[10px] drop-shadow-lg">
                          AI Score: 95/100
                        </p>
                      </div>
                      <div className="absolute bottom-3 right-3 text-[9px] text-white/60 bg-black/30 px-2 py-1 rounded-full">
                        Tap to reset
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* WORKER LEADERBOARD — hidden
      <section
        id="leaderboard"
        className="py-12 sm:py-16 px-6"
        data-testid="leaderboard-section"
      >
        <div className="max-w-[1200px] mx-auto">
          <div className="text-center mb-8">
            <span className="text-xs font-bold tracking-[0.2em] uppercase text-[#8B5CF6] mb-2 block">
              Top Performers
            </span>
            <h2
              className="text-2xl sm:text-3xl font-bold text-[#0A0A0A]"
              style={{ fontFamily: "Poppins" }}
            >
              Worker Leaderboard
            </h2>
            <p className="text-sm text-[#4B5563] mt-2">
              Ahmedabad's highest-rated home pros — rated by real customers.
            </p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {workers.slice(0, 6).map((w, idx) => (
              <div
                key={w.worker_id || w.user_id || idx}
                className="glass rounded-[16px] p-4 card-hover flex items-center gap-3 animate-fade-in-up"
                style={{ animationDelay: `${Math.min(idx * 60, 360)}ms` }}
                data-testid={`landing-worker-${idx}`}
              >
                <div className="relative shrink-0">
                  <img
                    src={w.image}
                    alt={w.name}
                    className="w-12 h-12 rounded-full object-cover ring-2 ring-white shadow-sm"
                  />
                  {idx < 3 && (
                    <div
                      className="absolute -top-1 -right-1 w-5 h-5 rounded-full flex items-center justify-center text-[8px] font-bold text-white"
                      style={{
                        background:
                          idx === 0
                            ? "#FFD84D"
                            : idx === 1
                              ? "#C0C0C0"
                              : "#CD7F32",
                      }}
                    >
                      #{idx + 1}
                    </div>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5">
                    <h4 className="font-semibold text-[#0A0A0A] text-sm truncate">
                      {w.name}
                    </h4>
                    {w.badge && (
                      <span className="trust-badge text-[8px]">{w.badge}</span>
                    )}
                  </div>
                  <div className="flex items-center gap-2 mt-0.5">
                    <span className="flex items-center gap-0.5">
                      <Star className="w-3 h-3 text-[#FFC93C] fill-[#FFC93C]" />
                      <span className="text-[10px] font-bold">{w.rating}</span>
                    </span>
                    <span className="text-[9px] text-[#9CA3AF]">
                      {w.jobs_completed} jobs
                    </span>
                    {w.area && (
                      <span className="text-[9px] text-[#9CA3AF]">
                        · {w.area}
                      </span>
                    )}
                  </div>
                </div>
                <Trophy
                  className="w-4 h-4 shrink-0"
                  style={{ color: idx === 0 ? "#FFD84D" : "#D1D5DB" }}
                />
              </div>
            ))}
          </div>
        </div>
      </section>
      */}

      {/* HOW IT WORKS */}
      <section className="py-12 sm:py-16 px-6">
        <div className="max-w-[1200px] mx-auto">
          <div className="text-center mb-10 scroll-reveal">
            <span className="text-xs font-bold tracking-[0.2em] uppercase text-[#8B5CF6] mb-2 block">
              Simple Process
            </span>
            <h2
              className="text-2xl sm:text-3xl font-bold text-[#0A0A0A]"
              style={{ fontFamily: "Poppins" }}
            >
              How It Works
            </h2>
          </div>
          <div className="grid sm:grid-cols-3 gap-6 relative">
            <div className="hidden sm:block absolute top-14 left-[20%] right-[20%] h-px bg-gradient-to-r from-[#6C4CF1]/15 via-[#FFD84D]/15 to-[#2EE6C9]/15" />
            {[
              {
                n: "01",
                t: "Choose Service",
                d: "Pick from 9+ services",
                icon: Sparkles,
                c: "#6C4CF1",
                bg: "bg-[#F3EEFF]",
              },
              {
                n: "02",
                t: "AI Matches Worker",
                d: "Smart AI finds the perfect pro",
                icon: Zap,
                c: "#FFC93C",
                bg: "bg-[#FFF8E1]",
              },
              {
                n: "03",
                t: "Sit Back & Relax",
                d: "Track and enjoy your clean home",
                icon: Check,
                c: "#2EE6C9",
                bg: "bg-[#E8FFF5]",
              },
            ].map((s, i) => (
              <div
                key={s.n}
                className={`scroll-reveal delay-${i + 1} text-center`}
              >
                <div
                  className={`w-14 h-14 rounded-full ${s.bg} flex items-center justify-center mx-auto mb-3 relative z-10`}
                >
                  <s.icon className="w-6 h-6" style={{ color: s.c }} />
                </div>
                <span className="text-xs font-bold text-[#9CA3AF] mb-1 block">
                  {s.n}
                </span>
                <h4 className="text-sm font-bold text-[#0A0A0A] mb-1">{s.t}</h4>
                <p className="text-xs text-[#4B5563] max-w-[200px] mx-auto">
                  {s.d}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* TESTIMONIALS — hidden
      <section id="reviews" className="py-12 sm:py-16 px-6">
        <div className="max-w-[1200px] mx-auto">
          <div className="text-center mb-8 scroll-reveal">
            <span className="text-xs font-bold tracking-[0.2em] uppercase text-[#8B5CF6] mb-2 block">
              Client Testimonials
            </span>
            <h2
              className="text-2xl sm:text-3xl font-bold text-[#0A0A0A]"
              style={{ fontFamily: "Poppins" }}
            >
              What Our Clients Say
            </h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
            {TESTIMONIALS.map((t, i) => (
              <div
                key={t.name}
                className={`scroll-reveal delay-${i + 1} glass rounded-[16px] p-5 card-hover`}
              >
                <div className="flex gap-0.5 mb-3">
                  {Array.from({ length: t.rating }).map((_, j) => (
                    <Star
                      key={j}
                      className="w-3.5 h-3.5 text-[#FFC93C] fill-[#FFC93C]"
                    />
                  ))}
                </div>
                <p className="text-xs text-[#4B5563] leading-relaxed mb-4">
                  "{t.text}"
                </p>
                <div className="flex items-center gap-2">
                  <img
                    src={t.img}
                    alt={t.name}
                    className="w-8 h-8 rounded-full"
                  />
                  <div>
                    <p className="text-xs font-semibold text-[#0A0A0A]">
                      {t.name}
                    </p>
                    <p className="text-[9px] text-[#9CA3AF]">{t.location}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
      */}

      {/* LEGAL / POLICIES */}
      <section
        id="policies"
        className="py-10 sm:py-14 px-6"
        data-testid="policies-section"
      >
        <div className="max-w-[1100px] mx-auto">
          <div className="text-center mb-6">
            <span className="text-xs font-bold tracking-[0.2em] uppercase text-[#8B5CF6] mb-2 block">
              Transparent
            </span>
            <h2
              className="text-2xl sm:text-3xl font-bold text-[#0A0A0A]"
              style={{ fontFamily: "Poppins" }}
            >
              Policies & Agreements
            </h2>
            <p className="text-sm text-[#4B5563] mt-2">
              Clear, plain-English policies. Read before you book.
            </p>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
            {POLICIES.map((p) => (
              <button
                key={p.slug}
                onClick={() => setPolicy(p.slug)}
                className="glass rounded-[16px] p-4 card-hover text-left group"
                data-testid={`policy-${p.slug}-btn`}
              >
                <div className="w-9 h-9 rounded-[10px] bg-[#F3EEFF] flex items-center justify-center mb-2 group-hover:bg-[#6C4CF1] transition-colors">
                  <p.icon className="w-4 h-4 text-[#6C4CF1] group-hover:text-white transition-colors" />
                </div>
                <p className="text-xs font-bold text-[#0A0A0A] mb-0.5">
                  {p.title}
                </p>
                <p className="text-[9px] text-[#9CA3AF]">Read →</p>
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section
        id="faq"
        className="py-12 sm:py-16 px-6"
        data-testid="faq-section"
      >
        <div className="max-w-[900px] mx-auto">
          <div className="text-center mb-8">
            <span className="text-xs font-bold tracking-[0.2em] uppercase text-[#8B5CF6] mb-2 block">
              Questions?
            </span>
            <h2
              className="text-2xl sm:text-3xl font-bold text-[#0A0A0A]"
              style={{ fontFamily: "Poppins" }}
            >
              Frequently Asked
            </h2>
            <p className="text-sm text-[#4B5563] mt-2">
              Still curious? Drop us a line at gharsetgo22@gmail.com.
            </p>
          </div>
          <div className="space-y-3">
            {FAQS.map((f, i) => {
              const open = openFaq === i;
              return (
                <div
                  key={f.q}
                  className={`glass rounded-[16px] overflow-hidden transition-all ${open ? "ring-1 ring-[#6C4CF1]/30" : ""}`}
                  data-testid={`faq-item-${i}`}
                >
                  <button
                    onClick={() => setOpenFaq(open ? -1 : i)}
                    className="w-full flex items-center justify-between gap-3 p-4 text-left"
                  >
                    <span className="flex items-center gap-2 text-sm font-semibold text-[#0A0A0A]">
                      <HelpCircle className="w-4 h-4 text-[#6C4CF1] shrink-0" />
                      {f.q}
                    </span>
                    <ChevronDown
                      className={`w-4 h-4 text-[#9CA3AF] shrink-0 transition-transform ${open ? "rotate-180" : ""}`}
                    />
                  </button>
                  {open && (
                    <div
                      className="px-4 pb-4 text-sm text-[#4B5563] leading-relaxed pl-10"
                      data-testid={`faq-answer-${i}`}
                    >
                      {f.a}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Founder's Note */}
      <FoundersNote />

      {/* UNIFIED PURPLE SECTION: Email + CTA */}
      <section className="py-12 sm:py-16 px-6" data-testid="newsletter-section">
        <div className="max-w-[1200px] mx-auto scroll-reveal">
          <div
            className="rounded-[28px] relative overflow-hidden"
            style={{
              background: "linear-gradient(135deg, #2D1B69, #6C4CF1, #8B5CF6)",
            }}
          >
            <div className="absolute top-0 right-0 w-48 h-48 bg-[#FFD84D]/10 rounded-full blur-3xl" />
            <div className="absolute bottom-0 left-0 w-40 h-40 bg-[#2EE6C9]/10 rounded-full blur-3xl" />
            <div
              className="absolute inset-0 opacity-10"
              style={{
                backgroundImage:
                  "radial-gradient(circle at 2px 2px, rgba(255,255,255,0.15) 1px, transparent 0)",
                backgroundSize: "20px 20px",
              }}
            />
            <div className="relative z-10">
              {/* Top: Email Subscribe */}
              <div className="p-8 sm:p-10 pb-6 text-center border-b border-white/10">
                <Mail className="w-7 h-7 text-[#FFD84D] mx-auto mb-2" />
                <h3
                  className="text-lg font-bold text-white mb-1"
                  style={{ fontFamily: "Poppins" }}
                >
                  Get Updates & Exclusive Offers
                </h3>
                <p className="text-[11px] text-white/50 mb-5">
                  Subscribe for deals, tips and new service launches.
                </p>
                {subscribed ? (
                  <div className="flex items-center justify-center gap-2">
                    <Check className="w-5 h-5 text-[#2EE6C9]" />
                    <span className="text-sm font-semibold text-white">
                      Subscribed!
                    </span>
                  </div>
                ) : (
                  <div
                    className="flex gap-2 max-w-sm mx-auto"
                    data-testid="email-form"
                  >
                    <input
                      type="email"
                      placeholder="Enter your email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="flex-1 px-4 py-2.5 rounded-full bg-white/10 border border-white/20 text-sm text-white placeholder-white/40 outline-none focus:border-white/50"
                      data-testid="email-input"
                    />
                    <button
                      data-testid="subscribe-btn"
                      onClick={() => {
                        if (email.includes("@")) setSubscribed(true);
                      }}
                      className="bg-[#FFD84D] text-[#0A0A0A] rounded-full px-5 py-2.5 text-sm font-bold flex items-center gap-1.5 shrink-0 hover:bg-[#FFE066] transition-colors"
                    >
                      <Send className="w-3.5 h-3.5" />
                      Subscribe
                    </button>
                  </div>
                )}
              </div>
              {/* Bottom: CTA */}
              <div
                className="p-8 sm:p-10 pt-6 text-center"
                data-testid="final-cta-section"
              >
                <h2
                  className="text-2xl sm:text-3xl font-bold text-white mb-2"
                  style={{ fontFamily: "Poppins" }}
                >
                  Your Home, Managed{" "}
                  <span className="text-[#FFD84D]">Smarter</span>
                </h2>
                <p className="text-white/60 text-sm mb-5 max-w-md mx-auto">
                  Join thousands of happy homeowners.
                </p>
                <button
                  data-testid="final-get-started-btn"
                  // onClick={login}
                  className="bg-white text-[#6C4CF1] rounded-full px-8 py-3 text-sm font-bold hover:-translate-y-0.5 transition-all shadow-lg inline-flex items-center gap-2 group"
                >
                  Get Started Now{" "}
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer
        className="py-8 px-6 border-t border-[#E5E7EB]/50"
        data-testid="footer-section"
      >
        <div className="max-w-[1200px] mx-auto">
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8 mb-6">
            <div>
              <Logo size={28} variant="full" className="mb-1" />
              <p className="text-[11px] italic text-[#6C4CF1] mb-2">
                Har kaam, bas set ho.
              </p>
              <p className="text-xs text-[#4B5563] mb-3">
                Ghar set, zindagi go.
              </p>
              <div className="flex gap-2 mb-3">
                {[Instagram, Twitter, Facebook, Linkedin].map((I, i) => (
                  <a
                    key={i}
                    href="#"
                    className="w-7 h-7 rounded-full glass flex items-center justify-center hover:bg-[#6C4CF1]/10"
                  >
                    <I className="w-3 h-3 text-[#4B5563]" />
                  </a>
                ))}
              </div>
              {/* Hidden admin access — admin login route disabled
              <button
                onClick={() => navigate("/admin/login")}
                className="text-[9px] text-[#D1D5DB] hover:text-[#6C4CF1] transition-colors tracking-wider"
                data-testid="hidden-admin-link"
                title="Admin"
              >
                · admin ·
              </button>
              */}
            </div>
            <div>
              <h5 className="font-semibold text-[#0A0A0A] mb-2 text-xs">
                Quick Links
              </h5>
              <ul className="space-y-1.5 text-xs text-[#4B5563]">
                {["About", "How It Works", "Pricing", "Contact"].map((l) => (
                  <li key={l}>
                    <a href="#" className="hover:text-[#6C4CF1]">
                      {l}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <h5 className="font-semibold text-[#0A0A0A] mb-2 text-xs">
                Services
              </h5>
              <ul className="space-y-1.5 text-xs text-[#4B5563]">
                {services.slice(0, 5).map((s) => (
                  <li key={s.service_id}>
                    <a href="#" className="hover:text-[#6C4CF1]">
                      {s.name}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <h5 className="font-semibold text-[#0A0A0A] mb-2 text-xs">
                Legal & Policies
              </h5>
              <ul className="space-y-1.5 text-xs text-[#4B5563]">
                {POLICIES.map((p) => (
                  <li key={p.slug}>
                    <button
                      onClick={() => setPolicy(p.slug)}
                      className="hover:text-[#6C4CF1] text-left"
                      data-testid={`footer-policy-${p.slug}`}
                    >
                      {p.title}
                    </button>
                  </li>
                ))}
                <li className="pt-1 flex items-center gap-1.5">
                  <Mail className="w-3 h-3 text-[#6C4CF1]" />
                  gharsetgo22@gmail.com
                </li>
              </ul>
            </div>
          </div>
          <div className="border-t border-[#E5E7EB]/50 pt-4 text-center text-[10px] text-[#9CA3AF]">
            &copy; {new Date().getFullYear()} GharSetGo. Made with ♥ in
            Ahmedabad.
          </div>
        </div>
      </footer>
      <a
        href={WHATSAPP_URL}
        target="_blank"
        rel="noopener noreferrer"
        aria-label="Chat with us on WhatsApp"
        title="Chat with us on WhatsApp"
        data-testid="whatsapp-fab"
        className="fixed bottom-6 right-6 w-12 h-12 bg-[#25D366] text-white rounded-full flex items-center justify-center shadow-lg z-50 hover:scale-105 transition-transform"
      >
        <svg
          viewBox="0 0 24 24"
          fill="currentColor"
          className="w-6 h-6"
          aria-hidden="true"
        >
          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884a9.82 9.82 0 0 1 6.988 2.896 9.83 9.83 0 0 1 2.893 6.994c-.003 5.45-4.437 9.885-9.885 9.885m8.413-18.297A11.82 11.82 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.88 11.88 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.82 11.82 0 0 0-3.48-8.413Z" />
        </svg>
      </a>
      {showTop && (
        <button
          data-testid="scroll-top-btn"
          onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
          className="fixed bottom-20 right-7 w-10 h-10 bg-[#0A0A0A] text-white rounded-full flex items-center justify-center shadow-lg z-50 animate-fade-in"
        >
          <ArrowUp className="w-4 h-4" />
        </button>
      )}
      {policy && <PolicyModal slug={policy} onClose={() => setPolicy(null)} />}
    </div>
  );
}

// Policy / legal modal — opens when any policy card is clicked
function PolicyModal({ slug, onClose }) {
  const doc = POLICY_CONTENT[slug];
  if (!doc) return null;
  return (
    <div
      className="fixed inset-0 z-[100] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 animate-fade-in"
      onClick={onClose}
      data-testid="policy-modal"
    >
      <div
        className="w-full max-w-2xl max-h-[85vh] bg-white rounded-[22px] overflow-hidden shadow-2xl flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-5 border-b border-[#E5E7EB] flex items-start justify-between bg-gradient-to-r from-[#F3EEFF] to-white">
          <div>
            <h3
              className="text-lg font-bold text-[#0A0A0A]"
              style={{ fontFamily: "Poppins" }}
            >
              {doc.title}
            </h3>
            <p className="text-[10px] text-[#9CA3AF] mt-0.5">{doc.updated}</p>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-full bg-white hover:bg-[#F3F4F6] flex items-center justify-center"
            data-testid="policy-close-btn"
          >
            <span className="text-lg leading-none text-[#4B5563]">×</span>
          </button>
        </div>
        <div className="p-6 overflow-y-auto flex-1 space-y-4">
          {doc.body.map(([h, p], i) => (
            <div key={i}>
              <h4
                className="text-sm font-bold text-[#0A0A0A] mb-1"
                style={{ fontFamily: "Poppins" }}
              >
                {h}
              </h4>
              <p className="text-[12px] text-[#4B5563] leading-relaxed">{p}</p>
            </div>
          ))}
          <p className="text-[10px] text-[#9CA3AF] pt-4 border-t border-[#E5E7EB]">
            GharSetGo Services Pvt. Ltd. · Ahmedabad, Gujarat, India
          </p>
        </div>
      </div>
    </div>
  );
}
